import { Field, IconButton, NumberInput, Switch, toast } from "@carbon6/c6-frontend-design";
import { useEffect, useRef, useState } from "react";
import {
  getDiagnosticScaling,
  saveDiagnosticScaling,
  type DiagnosticScalingPolicy,
  type DiagnosticScalingStatus,
} from "@/api/adminDiagnosticScaling";
import { adminOperationError } from "@/app/adminOperationError";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { usePolledRequest } from "./usePolledRequest";
import styles from "./DiagnosticScalingSettings.module.css";

const phaseLabels = {
  stable: "대기",
  increasing: "Worker 시작 대기",
  draining: "현재 작업 완료 대기",
  decreasing: "Worker 종료 대기",
  cancelling: "변경 취소 확인 중",
} satisfies Record<DiagnosticScalingStatus["operation"]["phase"], string>;

const numericFields = [
  { key: "idle_workers", label: "유휴 Worker 목표", min: 1, max: 30 },
  { key: "max_workers", label: "최대 Worker 수", min: 1, max: 30 },
  { key: "shrink_after_seconds", label: "축소 대기 시간(초)", min: 0 },
  { key: "observation_seconds", label: "관찰 주기(초)", min: 1 },
] as const;
type NumericKey = (typeof numericFields)[number]["key"];
type Draft = { enabled: boolean } & Record<NumericKey, number | null>;

function policyFromDraft(draft: Draft): DiagnosticScalingPolicy | null {
  const { enabled, idle_workers, max_workers, shrink_after_seconds, observation_seconds } = draft;
  if (
    idle_workers === null ||
    max_workers === null ||
    shrink_after_seconds === null ||
    observation_seconds === null
  )
    return null;
  if (
    ![idle_workers, max_workers, shrink_after_seconds, observation_seconds].every(
      Number.isSafeInteger,
    )
  )
    return null;
  if (
    idle_workers < 1 ||
    idle_workers > max_workers ||
    max_workers > 30 ||
    shrink_after_seconds < 0 ||
    observation_seconds < 1
  )
    return null;
  return { enabled, idle_workers, max_workers, shrink_after_seconds, observation_seconds };
}

export function DiagnosticScalingSettings() {
  const [reload, setReload] = useState(0);
  const { data, error } = usePolledRequest(getDiagnosticScaling, reload);
  const retry = () => setReload((value) => value + 1);
  return (
    <section className={styles.settings} aria-label="진단 자동 증감 설정">
      <h3>진단 Worker 자동 증감</h3>
      <p className={styles.note}>
        업로드 Worker 4개는 별도로 유지합니다. 이 설정은 진단 Worker에 적용됩니다.
      </p>
      {data === null ? (
        // 받은 값이 없는 실패는 폼 자리를 대신한다. 받지 못한 것을 대기로 표시하지 않는다.
        error ? (
          <EmptyState size="sm" tone="error" action={{ label: "다시 시도", onClick: retry }}>
            {error}
          </EmptyState>
        ) : (
          <LoadingState size="sm">설정을 불러오고 있습니다.</LoadingState>
        )
      ) : (
        <>
          {error && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          <ScalingForm
            key={data.revision}
            status={data}
            unavailable={error !== null}
            onSaved={retry}
          />
          {error && <IconButton text="다시 시도" size="sm" onClick={retry} />}
        </>
      )}
    </section>
  );
}

function ScalingForm({
  status,
  unavailable,
  onSaved,
}: {
  status: DiagnosticScalingStatus;
  unavailable: boolean;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(status.policy);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const locked = status.operation.phase !== "stable";
  const disabled = locked || busy || unavailable;
  const policy = policyFromDraft(draft);
  const save = async () => {
    if (disabled || policy === null) return;
    setBusy(true);
    setError(null);
    try {
      await saveDiagnosticScaling(status.revision, policy);
      if (alive.current) {
        toast.success("진단 Worker 설정을 저장했습니다.");
        onSaved();
      }
    } catch (caught) {
      if (alive.current) setError(adminOperationError(caught));
    } finally {
      if (alive.current) setBusy(false);
    }
  };
  return (
    <div className={styles.form}>
      <p>현재 적용: {status.policy.enabled ? "활성화" : "비활성화"}</p>
      <p role="status">
        {phaseLabels[status.operation.phase]}
        {status.operation.phase !== "stable" && ` · 목표 ${status.operation.target}개`}
      </p>
      {locked && (
        <p className={styles.note}>
          Worker 수를 변경하고 있습니다. Pod 상태를 확인한 뒤 설정을 변경할 수 있습니다.
        </p>
      )}
      {!locked && status.eligible_since !== null && (
        <p className={styles.note}>
          유휴 Worker가 목표보다 많습니다. 축소 대기 시간을 관찰하고 있습니다.
        </p>
      )}
      <Field label="자동 증감 활성화" layout="hug" labelPlacement="end">
        <Switch
          aria-label="자동 증감 활성화"
          checked={draft.enabled}
          disabled={disabled}
          onChange={(event) => setDraft({ ...draft, enabled: event.target.checked })}
        />
      </Field>
      {numericFields.map((field) => (
        <Field key={field.key} label={field.label} disabled={disabled}>
          <NumberInput
            aria-label={field.label}
            mode="integer"
            allowNegative={false}
            value={draft[field.key]}
            min={field.min}
            max={"max" in field ? field.max : undefined}
            disabled={disabled}
            onChange={(value) => setDraft((current) => ({ ...current, [field.key]: value }))}
          />
        </Field>
      ))}
      {policy === null && (
        <p role="alert" className={styles.error}>
          유휴 목표는 1개 이상, 최대 수 이하로 입력해 주세요. 최대 수는 30개입니다. 관찰 주기는 1초
          이상이어야 합니다.
        </p>
      )}
      {error && (
        <p role="alert" className={styles.error}>
          {error}
        </p>
      )}
      <IconButton
        text={busy ? "저장 중…" : "설정 저장"}
        size="sm"
        variant="solid"
        disabled={disabled || policy === null}
        onClick={() => void save()}
      />
    </div>
  );
}
